import React, { useEffect, useState } from "react";
import ProgressBar from "./ProgressBar.tsx";

const DEFAULT_MESSAGES = [
  "Designing figures…",
  "Working out the correct answers…",
  "Checking every question is unique…",
  "Rendering diagrams…",
];

interface GeneratingOverlayProps {
  active: boolean;
  title: string;
  numQuestions: number;
  messages?: string[];
}

// Fake progress bar for long-running generate calls — no real progress events from the
// backend, so we ease toward 90% over an estimate scaled by question count and let the
// overlay unmounting (on real completion) provide the "snap to done" moment.
const GeneratingOverlay: React.FC<GeneratingOverlayProps> = ({
  active,
  title,
  numQuestions,
  messages = DEFAULT_MESSAGES,
}) => {
  const [percent, setPercent] = useState(0);
  const [messageIdx, setMessageIdx] = useState(0);

  useEffect(() => {
    if (!active) {
      setPercent(0);
      setMessageIdx(0);
      return;
    }
    const durationMs = Math.min(90000, Math.max(8000, numQuestions * 8000));
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const ratio = Math.min((Date.now() - startedAt) / durationMs, 1);
      setPercent(Math.round(90 * (1 - Math.pow(1 - ratio, 2))));
      setMessageIdx(Math.floor((Date.now() - startedAt) / 4000) % messages.length);
    }, 300);
    return () => clearInterval(interval);
  }, [active, numQuestions, messages]);

  if (!active) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white bg-opacity-90 rounded-lg border border-border px-6">
      <span className="text-3xl font-semibold font-display text-ink">{title}</span>
      <span className="text-lg font-body text-gray-500 mt-3 mb-6">{messages[messageIdx]}</span>
      <div className="w-full max-w-md">
        <ProgressBar current={percent} total={100} />
      </div>
    </div>
  );
};

export default GeneratingOverlay;
